import type { ChinaPlaceCategory } from "#china-map-types";

/**
 * Ethnic-group names that autonomous regions, prefectures and counties carry between the place name
 * and the administrative suffix (`恩施土家族苗族自治州` → `恩施`). `蒙古族` is listed before `蒙古` so
 * the longer name wins.
 */
const ETHNIC_GROUP_NAMES = [
  "土家族",
  "苗族",
  "朝鲜族",
  "布依族",
  "侗族",
  "彝族",
  "哈尼族",
  "壮族",
  "傣族",
  "白族",
  "景颇族",
  "傈僳族",
  "藏族",
  "回族",
  "羌族",
  "蒙古族",
  "蒙古",
  "柯尔克孜",
  "哈萨克",
  "维吾尔",
  "仡佬族",
  "水族",
  "瑶族",
  "畲族",
  "黎族",
  "满族",
  "土族",
  "撒拉族",
  "东乡族",
  "保安族",
  "裕固族",
  "达斡尔族",
  "鄂温克族",
  "鄂伦春族",
  "锡伯族",
  "纳西族",
  "普米族",
  "独龙族",
  "怒族",
  "拉祜族",
  "佤族",
  "布朗族",
  "阿昌族",
  "基诺族",
  "德昂族",
  "毛南族",
  "仫佬族",
  "各族",
];

const ADMINISTRATIVE_SUFFIXES: readonly (readonly [string, ChinaPlaceCategory])[] = [
  ["特别行政区", "special-administrative-region"],
  ["自治区", "autonomous-region"],
  ["自治州", "autonomous-prefecture"],
  ["自治县", "county"],
  ["地区", "prefecture"],
  ["林区", "forestry-district"],
  ["新区", "district"],
  ["省", "province"],
  ["盟", "league"],
  ["县", "county"],
  ["区", "district"],
  ["市", "city"],
];

/**
 * Splits an official region name into its everyday short name and its category. Short names keep
 * at least two characters, so single-character stems keep their suffix (`忠县`, `东区`).
 */
export function parseChinaPlaceName(
  name: string,
  { isProvinceLevel }: { isProvinceLevel: boolean },
): { category: ChinaPlaceCategory; shortName: string } {
  const suffix = ADMINISTRATIVE_SUFFIXES.find(([candidate]) => name.endsWith(candidate));
  // Province-level `市` are the four direct-administered municipalities.
  const category: ChinaPlaceCategory =
    suffix === undefined
      ? "city"
      : isProvinceLevel && suffix[1] === "city"
        ? "municipality"
        : suffix[1];
  let shortName = suffix === undefined ? name : name.slice(0, -suffix[0].length);
  let stripped = true;
  while (stripped) {
    stripped = false;
    for (const ethnicGroupName of ETHNIC_GROUP_NAMES) {
      if (shortName.length - ethnicGroupName.length >= 2 && shortName.endsWith(ethnicGroupName)) {
        shortName = shortName.slice(0, -ethnicGroupName.length);
        stripped = true;
      }
    }
  }
  return { category, shortName: shortName.length >= 2 ? shortName : name };
}
